<template>
  <PageContainer v-if="fields" class="bg-mint" collapse-header>
    <template #header>
      <!--
        The burst is positioned against this wrapper, so the wrapper has to own the header
        padding that PageContainer would otherwise apply (hence collapse-header). Without
        that the artwork starts below the nav and leaves a flat band behind it.
      -->
      <div class="relative pt-52 pb-8 md:pt-56">
        <DefaultGrid class="relative z-2 gap-y-10">
          <div class="col-span-full space-y-10">
            <PayloadTextArea
              tag="h1"
              class="type-heading-jumbo type-font-salt -rotate-5 text-center"
              :content="fields.header.heading"
            />
            <p
              class="mx-auto max-w-[780px] text-center text-lg font-normal md:text-[22px]/[28px]"
            >
              Wait Mate helps parents wait before giving their kids a
              smartphone, together. Come along to a free parent session to hear
              what the research says, ask your questions and meet other local
              families.
            </p>
          </div>
          <div class="col-span-full mt-1.25 space-y-20 md:space-y-24">
            <div>
              <p
                class="mx-auto max-w-[538px] text-center text-[16px]/[28px] font-medium"
                v-text="fields.header.body"
              />
              <HomeSchoolSearch
                class="mt-5.5"
                sa-only
                :initial-query="initialQuery"
              />
            </div>
            <div class="flex items-center justify-center gap-x-5">
              <h3>Supported by</h3>
              <img
                src="/img/sa/sa-government-logo.png"
                alt="Government of South Australia"
                class="h-9 w-auto"
              />
            </div>
          </div>
        </DefaultGrid>
        <div
          class="absolute inset-0 flex items-center justify-center overflow-hidden"
        >
          <SvgBurst class="text-blue flex-none" />
        </div>
      </div>
    </template>
    <template #main>
      <section class="bg-mint">
        <DefaultGrid class="pb-20">
          <div class="col-span-full">
            <MuxVideoPlayer
              class="aspect-video rounded-3xl"
              playback-id="7BZrjtgn4KBOFPqhF8pLRd9uEWvp3QEQ901aN3xDpu0100"
              :gif-start="40"
            />
          </div>
        </DefaultGrid>
      </section>
      <div class="from-mint bg-linear-to-b from-50% to-white to-50%">
        <section class="bg-grey rounded-[40px] py-20 text-black md:py-40">
          <DefaultGrid>
            <div class="col-span-full mb-10 space-y-10">
              <h2
                class="type-heading-1 type-font-salt-ss02 text-center"
                v-text="fields.statisticsSection.heading"
              />
            </div>
            <div
              class="col-span-full mb-20 md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3 2xl:col-span-6 2xl:col-start-4"
            >
              <PayloadTextArea
                class="text-center text-lg font-normal md:text-xl"
                :content="fields.statisticsSection.body"
              />
            </div>
            <div
              class="col-span-full flex flex-col gap-4 md:grid md:grid-cols-4 lg:flex lg:flex-row lg:gap-x-18 xl:col-span-10 xl:col-start-2"
            >
              <div
                v-for="(card, idx) in fields.statisticsSection.cards"
                :key="idx"
                class="md:col-span-2 md:last:col-start-2 lg:min-w-0 lg:flex-1"
              >
                <HomeStatCard
                  class="min-h-0 p-8 shadow-md transition-transform duration-300 hover:scale-110 md:aspect-square"
                  :class="[
                    getCardBackgroundClass(idx),
                    hoverClasses[idx % hoverClasses.length],
                  ]"
                  :label="card.heading"
                  :stat="card.stat"
                />
              </div>
            </div>
          </DefaultGrid>
        </section>
      </div>
      <PageShingleWrapper
        class="relative z-2 bg-white py-20"
        next-class="bg-white"
      >
        <DefaultGrid class="gap-y-16 md:gap-y-20">
          <div class="col-span-full flex justify-center">
            <div class="type-heading-1 relative">
              <PayloadIcon
                icon="SvgIconAttentionC"
                class="text-yellow-light absolute bottom-[-1.2em] left-[-0.9em] h-auto w-[1.5em] rotate-15 md:bottom-[-1.2em] md:left-[-1em] md:w-[1.2em] lg:left-[-1.8em]"
              />
              <h2
                class="type-font-salt-ss02 text-center"
                v-text="fields.faqSection.heading"
              />
              <PayloadIcon
                icon="SvgIconStar"
                class="text-mint absolute top-[-0.5em] right-[0.3em] w-[0.8em] rotate-11 md:top-[-0.4em] md:right-[-0.5em] md:w-[0.6em] lg:right-[-0.7em]"
              />
            </div>
          </div>
          <div
            class="relative z-2 col-span-full space-y-2 lg:col-span-8 lg:col-start-3 2xl:col-span-6 2xl:col-start-4"
          >
            <HomeFaqItem
              v-for="(item, idx) in fields.faqSection.items"
              :key="idx"
              :question="item.question"
              :answer="item.answer"
              :index="idx"
              section="sa_faq"
            />
          </div>
        </DefaultGrid>
      </PageShingleWrapper>
      <div class="bg-white">
        <section class="bg-cream overflow-hidden rounded-[40px] pt-20 md:pt-40">
          <DefaultGrid>
            <div class="col-span-full mb-10 space-y-10">
              <PayloadTextArea
                tag="h2"
                class="type-heading-1 type-font-salt-ss02 text-center"
                :content="fields.premierSection.heading"
              />
            </div>
            <div
              class="col-span-full mb-20 md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3 lg:mb-24 2xl:col-span-6 2xl:col-start-4"
            >
              <PayloadTextArea
                class="text-center text-lg font-normal md:text-xl"
                :content="fields.premierSection.body"
              />
            </div>
            <div
              class="col-span-full md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3 2xl:col-span-6 2xl:col-start-4"
            >
              <SaPremiersLetter :content="fields.premierSection.letter" />
            </div>
          </DefaultGrid>
        </section>
      </div>
    </template>
  </PageContainer>
</template>

<script setup lang="ts">
const doc = await usePayloadPage('SaMicrosite')

/**
 * `?search=` arrives on redirects from a slug that several schools once shared: the visitor
 * clicked a link naming their school, and only they can say which one was meant. Seeding the
 * search puts those candidates in front of them, and picking one goes straight to its page.
 */
const route = useRoute()
const initialQuery = computed(
  () => (route.query.search as string | undefined) || undefined,
)

const fields = doc.value?.saMicrositeTemplateFields

const { getCardBackgroundClass } = useGlobalsStore()

const hoverClasses = ['hover:rotate-4', 'hover:-rotate-4']

useSeoMeta({
  title: doc.value?.meta?.title ?? 'Wait Mate – South Australia',
  titleTemplate: '',
})
</script>
